import type { Option } from "@tento-lona/sheets";
import { NaiveDate } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import { LonaWebComponent, html } from "@tento-ui/component";
import { Div } from "@tento-ui/component-builtin";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER, offset } from "@tento-ui/gesture-manager";
import { LiveData } from "@tento-core/livedata";
import { MathUtils } from "@tento-core/math";
import { Point } from "@tento-core/point";

/** The inline style properties a reusable background day may carry (anything else: rebuild). */
const BACKGROUND_DAY_STYLE_PROPERTIES: readonly string[] = [
  "flex-grow", "background", "background-color", "background-image",
  "background-position", "background-position-x", "background-position-y",
  "background-size", "background-repeat", "background-repeat-x", "background-repeat-y",
  "background-attachment", "background-origin", "background-clip",
  "border-top-left-radius", "border-bottom-left-radius",
  "border-top-right-radius", "border-bottom-right-radius",
];

/** One column of a timeline, positioned for a date range and width. */
@component({
  name: "std-timeline-column",
})
export class TimelineColumnElement<Info> extends LonaWebComponent {
  #info: Option<Info> = null;
  #range: Option<NaiveDate.Partial> = null;
  #widthPx: Option<number> = null;
  #$highlight: HTMLElement = this.$("highlight");
  #$backgroundDays: HTMLDivElement[] = [];
  #hoveredDateLiveData: Option<LiveData.Readable<Option<NaiveDate>>> = null;

  static makeWith<Info>(
    delegate: TimelineColumnElement.Callbacks<Info>,
  ): TimelineColumnElement<Info> {
    const $c = TimelineColumnElement.make() as TimelineColumnElement<Info>;
    $c.setCallbacks(delegate);
    return $c;
  }

  setCallbacks(callbacks: TimelineColumnElement.Callbacks<Info>) {
    GESTURE_MANAGER.addPointerEvent(this, {
      onPointerDown: (offset, e) => {
        const position = this.#calculatePointerPosition(offset);
        // Don't capture here - let onPointerMove handle capture based on gesture needs.
        // This allows handle-based gestures (resize) to maintain their own capture
        // without the column stealing it.
        callbacks.onPointerDown({
          info: nonnull(this.#info),
          position,
        });
      },
      onClick: (_, offset) => {
        callbacks.onPointerUp({
          info: nonnull(this.#info),
          position: this.#calculatePointerPosition(offset),
        });
      },
      onContextMenu: (_, offset) => {
        callbacks.onContextMenu({
          info: nonnull(this.#info),
          position: this.#calculatePointerPosition(offset),
          offset,
        });
      },
    });
    this.onpointermove = (e) => {
      e.stopPropagation();
      const pointerOffset = offset(e);
      const position = this.#calculatePointerPosition(pointerOffset);
      const result = callbacks.onPointerMove({
        info: nonnull(this.#info),
        position,
      });
      if (result.shouldCapturePointer && !this.hasPointerCapture(e.pointerId)) {
        this.setPointerCapture(e.pointerId);
      }
    };
    this.onpointerleave = () => {
      callbacks.onPointerLeave?.();
    };
  }

  /**
   * Subscribe to a hovered date LiveData for rendering hover highlights
   * (tento-ui-js's `TimelineLayoutManager.hoveredDate` through
   * `optionLiveData`).
   */
  setHoveredDate(hoveredDate: LiveData.Readable<Option<NaiveDate>>): void {
    // Remove old listener
    this.#hoveredDateLiveData?.removeListener(this);
    this.#hoveredDateLiveData = hoveredDate;

    // Subscribe to changes with immediate trigger
    hoveredDate.addListener(
      this,
      (date) => {
        this.#updateHighlight(date);
      },
      LiveData.TriggerImmediately,
    );
  }

  #updateHighlight(date: Option<NaiveDate>): void {
    if (!date || !this.#range || !this.#widthPx) {
      this.#$highlight.style.display = "none";
      return;
    }

    const range = this.#range.toRange().iter();
    const idx = range.findIndex((d) => d.equals(date));

    if (idx === -1) {
      this.#$highlight.style.display = "none";
      return;
    }

    const cellWidthPx = this.#widthPx / range.length;
    this.#$highlight.assignStyles({
      display: "block",
      left: `${idx * cellWidthPx}px`,
      width: `${cellWidthPx}px`,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#hoveredDateLiveData?.removeListener(this);
  }

  #calculatePointerPosition(
    offset: Point,
  ): TimelineColumnElement.PointerPosition {
    const range = nonnull(this.#range, "TimelineColumnElement: pointer before bind").toRange().iter();
    const cellWidthPx = nonnull(this.#widthPx, "TimelineColumnElement: pointer before bind") / range.length;
    const xIdx = MathUtils.clampInc(
      Math.floor(offset.x / cellWidthPx),
      0,
      range.length - 1,
    );
    const date = nonnull(range[xIdx], "TimelineColumnElement: the clamped column index is in range");

    // Calculate relative offset within the cell (0.0 to 1.0)
    const cellStartPx = xIdx * cellWidthPx;
    const relativeOffset = MathUtils.clampInc(
      (offset.x - cellStartPx) / cellWidthPx,
      0,
      1,
    );

    return {
      date: NaiveDate.Partial.fromDate(date),
      relativeOffset,
      absoluteX: offset.x,
      cellWidthPx,
    };
  }

  #ownsBackgroundDays($bg: HTMLElement): boolean {
    if ($bg.childNodes.length !== this.#$backgroundDays.length) return false;
    for (const [index, $day] of this.#$backgroundDays.entries()) {
      if (
        $bg.childNodes[index] !== $day || $day.parentElement !== $bg ||
        $day.tagName !== "DIV" || $day.childNodes.length !== 0 ||
        $day.classList.length !== 0
      ) return false;
      for (const attribute of $day.attributes) {
        if (attribute.name !== "style" && attribute.name !== "dbg-dow") return false;
      }
      for (const property of $day.style) {
        if (
          !BACKGROUND_DAY_STYLE_PROPERTIES.includes(property) ||
          $day.style.getPropertyPriority(property) !== ""
        ) return false;
      }
    }
    return true;
  }

  bind(info: Info, props: TimelineColumnElement.Props) {
    this.#info = info;
    this.#widthPx = props.widthPx;
    this.#range = props.column;
    this.style.width = this.#widthPx + "px";

    const $bg = this.$("background");
    if (!this.#ownsBackgroundDays($bg)) {
      DomUtils.clearChildren($bg);
      this.#$backgroundDays = [];
    }

    const range = props.column.toRange().iter();
    while (this.#$backgroundDays.length > range.length) {
      const $day = this.#$backgroundDays.pop();
      if ($day) DomUtils.removeFromDom($day);
    }
    let index = 0;
    for (const d of range) {
      const dow = d.dayOfWeek;
      // The weekend tint delimits weekday/weekend transitions: corners
      // round only where a weekend RUN starts/ends against a weekday
      // (calendar neighbors, not fixed Sat/Sun — so Sunday-start weeks
      // keep a flat shared edge between prev-week Sat and this-week
      // Sun, reading as one continuous pill across the week divider).
      const startsRun = dow.isWeekend && !d.addDays(-1).dayOfWeek.isWeekend;
      const endsRun = dow.isWeekend && !d.addDays(1).dayOfWeek.isWeekend;

      // Positional decorations keep their DOM while each bind refreshes dates.
      const $colBg = this.#$backgroundDays[index] ?? Div.make();
      if (index === this.#$backgroundDays.length) this.#$backgroundDays.push($colBg);
      $colBg.assignStyles({
        flexGrow: "1",
        background: dow.isWeekend ? "var(--weekend-color)" : "",
        borderTopLeftRadius: startsRun ? "7px" : "0px",
        borderBottomLeftRadius: startsRun ? "7px" : "0px",
        borderTopRightRadius: endsRun ? "7px" : "0px",
        borderBottomRightRadius: endsRun ? "7px" : "0px",
      });
      if ($colBg.getAttribute("dbg-dow") !== dow.shortname) {
        $colBg.setAttribute("dbg-dow", dow.shortname);
      }

      if ($colBg.parentElement !== $bg) $bg.appendChild($colBg);
      index += 1;
    }
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        height: 100%;
        width: 100%;
        --weekend-color: var(--bg-hover);
      }

      #root {
        position: relative;
        height: 100%;
        width: 100%;
      }

      #background {
        position: absolute;
        top: 0;
        left: 0;
        pointer-events: none;
        height: calc(100% - 4px);
        width: 100%;
      }

      #highlight {
        position: absolute;
        top: 0;
        height: 100%;
        background: rgba(0, 102, 204, 0.05);
        pointer-events: none;
        display: none;
        border-radius: 8px;
      }
    `,
  ];
  static $html: Option<HTMLTemplateElement> = html`
    <div id="root">
      <std-row id="background"></std-row>
      <div id="highlight"></div>
    </div>
  `;
}

/**
 * Types that go with `TimelineColumnElement`: `Props`, `PointerPosition`,
 * `Callbacks`, `CellCallbacks`.
 */
export namespace TimelineColumnElement {
  export type Props = {
    column: NaiveDate.Partial;
    widthPx: number;
  };

  export type PointerPosition = {
    date: NaiveDate.Partial;
    relativeOffset: number; // 0.0 to 1.0 within the cell
    absoluteX: number; // Pixel position for rendering
    cellWidthPx: number; // Pixels per day cell, for calculating deltas
  };

  export type Callbacks<Info> = {
    onPointerDown: (event: { info: Info; position: PointerPosition }) => void;
    onPointerUp: (event: { info: Info; position: PointerPosition }) => void;
    onPointerMove: (event: { info: Info; position: PointerPosition }) => {
      shouldCapturePointer: boolean;
    };
    onContextMenu: (event: {
      info: Info;
      position: PointerPosition;
      offset: Point;
    }) => void;
    onPointerLeave?: () => void;
  };

  export type CellCallbacks = {
    onPointerDown: (event: {
      position: PointerPosition;
      handle: Option<"left" | "right">;
    }) => void;
    onPointerUp: (event: { position: PointerPosition }) => void;
    onPointerMove: (event: { position: PointerPosition }) => {
      shouldCapturePointer: boolean;
    };
    onContextMenu: (event: {
      position: PointerPosition;
      offset: Point;
    }) => void;
  };
}
